import { create } from 'zustand';
import { getSites, getZones, getFloors } from '@/api/sites';
import type { SiteResponse, ZoneResponse, FloorResponse } from '@/types/site';

interface SiteState {
  // Data
  sites: SiteResponse[];
  zones: ZoneResponse[];
  floors: FloorResponse[];
  isLoading: boolean;
  sitesError: string | null;
  catalogueGeneration: number;
  sitesRequest: number;

  // Selection
  selectedSite: string | null;
  selectedZone: string | null;
  selectedFloor: string | null;
  generation: number;
  reset: () => void;

  // Actions
  fetchSites: () => Promise<void>;
  fetchZones: (siteId: string) => Promise<void>;
  fetchFloors: (siteId: string, zoneId: string) => Promise<void>;
  setSelectedSite: (id: string) => void;
  setSelectedZone: (id: string) => void;
  setSelectedFloor: (id: string) => void;
}

export const useSiteStore = create<SiteState>((set, get) => ({
  sites: [],
  zones: [],
  floors: [],
  isLoading: false,
  sitesError: null,
  catalogueGeneration: 0,
  sitesRequest: 0,
  selectedSite: null,
  selectedZone: null,
  selectedFloor: null,
  generation: 0,
  reset: () => set((s) => ({ sites: [], zones: [], floors: [], selectedSite: null,
    selectedZone: null, selectedFloor: null, isLoading: false, sitesError: null,
    generation: s.generation + 1, catalogueGeneration: s.catalogueGeneration + 1,
    sitesRequest: s.sitesRequest + 1 })),

  fetchSites: async () => {
    const catalogueGeneration = get().catalogueGeneration;
    const request = get().sitesRequest + 1;
    set({ isLoading: true, sitesError: null, sitesRequest: request });
    try {
      const data = await getSites();
      if (get().catalogueGeneration !== catalogueGeneration || get().sitesRequest !== request) return;
      const sites = data.items;
      set({ sites, isLoading: false });
      // Auto-select first site if none selected
      if (!get().selectedSite && sites.length > 0) {
        get().setSelectedSite(sites[0].id);
      } else if (get().selectedSite && !sites.some((s) => s.id === get().selectedSite)) {
        if (sites[0]) get().setSelectedSite(sites[0].id);
        else get().reset();
      }
    } catch (error) {
      if (get().catalogueGeneration !== catalogueGeneration || get().sitesRequest !== request) return;
      set({ isLoading: false, sitesError: error instanceof Error ? error.message : '현장 목록을 불러오지 못했습니다.' });
    }
  },

  fetchZones: async (siteId) => {
    const generation = get().generation;
    try {
      const zones = await getZones(siteId);
      if (get().generation !== generation || get().selectedSite !== siteId) return;
      set({ zones });
      // Auto-select first zone
      if (zones.length > 0) {
        set({ selectedZone: zones[0].id, floors: [] });
        get().fetchFloors(siteId, zones[0].id);
      } else {
        set({ selectedZone: null, floors: [], selectedFloor: null });
      }
    } catch {
      if (get().generation !== generation || get().selectedSite !== siteId) return;
      set({ zones: [], selectedZone: null });
    }
  },

  fetchFloors: async (siteId, zoneId) => {
    const generation = get().generation;
    try {
      const floors = await getFloors(siteId, zoneId);
      if (get().generation !== generation || get().selectedSite !== siteId || get().selectedZone !== zoneId) return;
      set({ floors });
      if (floors.length > 0) {
        set({ selectedFloor: floors[0].id });
      } else {
        set({ selectedFloor: null });
      }
    } catch {
      if (get().generation !== generation || get().selectedSite !== siteId || get().selectedZone !== zoneId) return;
      set({ floors: [], selectedFloor: null });
    }
  },

  setSelectedSite: (id) => {
    if (get().selectedSite === id) return;
    set((s) => ({ selectedSite: id, selectedZone: null, selectedFloor: null, zones: [], floors: [], generation: s.generation + 1 }));
    get().fetchZones(id);
  },

  setSelectedZone: (id) => {
    set({ selectedZone: id, selectedFloor: null, floors: [] });
    const siteId = get().selectedSite;
    if (siteId) get().fetchFloors(siteId, id);
  },

  setSelectedFloor: (id) => set({ selectedFloor: id }),
}));
